export type VideoDebugSnapshot = {
  exists: boolean;
  isConnected: boolean;
  readyState: number;
  networkState: number;
  paused: boolean;
  muted: boolean;
  playsInline: boolean;
  currentTime: number;
  videoWidth: number;
  videoHeight: number;
  hasSrcObject: boolean;
  srcObjectTracks: number;
  srcObjectTrackKinds: string;
  errorCode: number | null;
  clientWidth: number;
  clientHeight: number;
  rectLeft: number;
  rectTop: number;
  rectWidth: number;
  rectHeight: number;
  stylePosition: string;
  styleLeft: string;
  styleTop: string;
  styleWidth: string;
  styleHeight: string;
  styleObjectFit: string;
  styleObjectPosition: string;
  styleTransform: string;
};

export type VideoDebugLogEntry = {
  ts: string;
  level: 'info' | 'warn' | 'error';
  source: 'app' | 'video' | 'console';
  event: string;
  msg: string;
  traceId: string;
  detail?: string;
};

type VideoDebugLogListener = (entries: VideoDebugLogEntry[]) => void;

const VIDEO_DEBUG_LOG_MAX_ENTRIES = 140;
const videoDebugLogEntries: VideoDebugLogEntry[] = [];
const videoDebugLogListeners = new Set<VideoDebugLogListener>();
const VIDEO_DEBUG_CAPTURE_FLAG = '__dwellbot_video_debug_capture_installed__';

const isTruthy = (value: string | null): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
};

export const isVideoDebugEnabledFromLocation = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const searchParams = new URLSearchParams(window.location.search);
  if (isTruthy(searchParams.get('video_debug'))) {
    return true;
  }
  try {
    return isTruthy(window.localStorage.getItem('video_debug'));
  } catch {
    return false;
  }
};

export const readVideoDebugSnapshot = (video: HTMLVideoElement | null): VideoDebugSnapshot => {
  const mediaTracks = video?.srcObject instanceof MediaStream ? video.srcObject.getTracks() : [];
  const rect = video?.getBoundingClientRect();
  return {
    exists: Boolean(video),
    isConnected: Boolean(video?.isConnected),
    readyState: video?.readyState ?? 0,
    networkState: video?.networkState ?? 0,
    paused: video?.paused ?? true,
    muted: video?.muted ?? false,
    playsInline: video?.playsInline ?? false,
    currentTime: Number((video?.currentTime ?? 0).toFixed(2)),
    videoWidth: video?.videoWidth ?? 0,
    videoHeight: video?.videoHeight ?? 0,
    hasSrcObject: Boolean(video?.srcObject),
    srcObjectTracks: mediaTracks.length,
    srcObjectTrackKinds: mediaTracks.map((track: MediaStreamTrack) => track.kind).join(','),
    errorCode: video?.error?.code ?? null,
    clientWidth: video?.clientWidth ?? 0,
    clientHeight: video?.clientHeight ?? 0,
    rectLeft: Number((rect?.left ?? 0).toFixed(1)),
    rectTop: Number((rect?.top ?? 0).toFixed(1)),
    rectWidth: Number((rect?.width ?? 0).toFixed(1)),
    rectHeight: Number((rect?.height ?? 0).toFixed(1)),
    stylePosition: video?.style.position ?? '',
    styleLeft: video?.style.left ?? '',
    styleTop: video?.style.top ?? '',
    styleWidth: video?.style.width ?? '',
    styleHeight: video?.style.height ?? '',
    styleObjectFit: video?.style.objectFit ?? '',
    styleObjectPosition: video?.style.objectPosition ?? '',
    styleTransform: video?.style.transform ?? '',
  };
};

const emitVideoDebugLogEntries = () => {
  const snapshot = [...videoDebugLogEntries];
  videoDebugLogListeners.forEach((listener) => {
    listener(snapshot);
  });
};

export const pushVideoDebugLogEntry = (entry: VideoDebugLogEntry) => {
  videoDebugLogEntries.push(entry);
  if (videoDebugLogEntries.length > VIDEO_DEBUG_LOG_MAX_ENTRIES) {
    videoDebugLogEntries.splice(0, videoDebugLogEntries.length - VIDEO_DEBUG_LOG_MAX_ENTRIES);
  }
  emitVideoDebugLogEntries();
};

export const subscribeVideoDebugLogEntries = (listener: VideoDebugLogListener): (() => void) => {
  videoDebugLogListeners.add(listener);
  listener([...videoDebugLogEntries]);
  return () => {
    videoDebugLogListeners.delete(listener);
  };
};

const formatConsoleArg = (arg: unknown): string => {
  if (arg instanceof Error) {
    if (arg.stack) {
      return `${arg.name}: ${arg.message} ${arg.stack}`;
    }
    return `${arg.name}: ${arg.message}`;
  }
  if (typeof arg === 'string') {
    return arg;
  }
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

const toConsoleLevel = (method: 'error' | 'warn' | 'info' | 'log'): 'error' | 'warn' | 'info' => {
  if (method === 'error') {
    return 'error';
  }
  if (method === 'warn') {
    return 'warn';
  }
  return 'info';
};

export const installVideoDebugRuntimeCapture = (traceId: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isVideoDebugEnabledFromLocation()) {
    return;
  }
  const markerHost = window as Window & { [VIDEO_DEBUG_CAPTURE_FLAG]?: boolean };
  if (markerHost[VIDEO_DEBUG_CAPTURE_FLAG]) {
    return;
  }
  markerHost[VIDEO_DEBUG_CAPTURE_FLAG] = true;

  const methods: Array<'error' | 'warn' | 'info' | 'log'> = ['error', 'warn', 'info', 'log'];
  methods.forEach((method) => {
    // eslint-disable-next-line no-console
    const original = console[method].bind(console);
    // eslint-disable-next-line no-console
    console[method] = (...args: unknown[]) => {
      const renderedArgs = args.map(formatConsoleArg);
      pushVideoDebugLogEntry({
        ts: new Date().toISOString(),
        level: toConsoleLevel(method),
        source: 'console',
        event: `console.${method}`,
        msg: renderedArgs[0] ?? `console ${method}`,
        traceId,
        detail: renderedArgs.length > 1 ? renderedArgs.slice(1).join(' | ') : undefined,
      });
      // eslint-disable-next-line no-console
      original(...args);
    };
  });

  window.addEventListener('error', (event) => {
    const detail = [
      event.filename ? `file:${event.filename}` : null,
      Number.isFinite(event.lineno) ? `line:${event.lineno}` : null,
      Number.isFinite(event.colno) ? `col:${event.colno}` : null,
      event.error ? formatConsoleArg(event.error) : null,
    ]
      .filter((part) => Boolean(part))
      .join(' ');
    pushVideoDebugLogEntry({
      ts: new Date().toISOString(),
      level: 'error',
      source: 'console',
      event: 'window.error',
      msg: event.message || 'Unhandled error',
      traceId,
      detail: detail || undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    pushVideoDebugLogEntry({
      ts: new Date().toISOString(),
      level: 'error',
      source: 'console',
      event: 'window.unhandledrejection',
      msg: 'Unhandled promise rejection',
      traceId,
      detail: formatConsoleArg(event.reason),
    });
  });

  pushVideoDebugLogEntry({
    ts: new Date().toISOString(),
    level: 'info',
    source: 'console',
    event: 'video_debug.capture_installed',
    msg: 'Runtime console/error capture enabled',
    traceId,
  });
};
